<template>
	<div class="temp-control-container">
		<section class="temp-control-section">
			<div class="container">
				<!-- Section Header -->
				<header class="section-header center">
					<h2 class="section-title">{{ $t('refrigeratedGoods.title') }}</h2>
					<p class="subsection-subtitle">{{ $t('refrigeratedGoods.subtitle') }}</p>
				</header>

				<!-- Solution Introduction -->
				<div class="solution-intro">
					<p class="intro-text">{{ $t('refrigeratedGoods.introduction') }}</p>
				</div>

				<!-- Features Grid -->
				<div class="features-grid">
					<div v-for="(feature, index) in $t('refrigeratedGoods.features')" :key="`feature-${index}`"
						class="feature-card">
						<div class="feature-icon" :style="{ backgroundColor: feature.color }">
							<i :class="feature.icon"></i>
						</div>
						<h3 class="feature-title">{{ feature.title }}</h3>
						<p class="feature-desc">{{ feature.description }}</p>
					</div>
				</div>

				<!-- Active Control Section -->
				<section class="active-control-section">
					<header class="section-header">
						<h3 class="subsection-title">{{ $t('refrigeratedGoods.activeControl.title') }}</h3>
						<p class="subsection-subtitle">{{ $t('refrigeratedGoods.activeControl.subtitle') }}</p>
					</header>

					<div class="equipment-showcase">
						<div v-for="(equip, index) in $t('refrigeratedGoods.activeControl.equipment')" :key="`equip-${index}`"
							class="equipment-card">
							<div class="equip-image-container">
								<div class="image-wrapper">
									<img :src="getImage(equip.image)" :alt="equip.name" class="equip-image" loading="lazy"
										@error="handleImageError">
								</div>
							</div>
							<div class="equip-info">
								<h4 class="equip-name">{{ equip.name }}</h4>
								<p class="equip-desc">{{ equip.description }}</p>
								<div class="equip-specs">
									<span v-for="(spec, i) in equip.specs" :key="`spec-${i}`" class="spec-item">
										<i :class="['spec-icon', spec.icon]"></i> {{ spec.text }}
									</span>
								</div>
								<button v-if="equip.cta" class="equip-cta">
									{{ equip.cta }}
								</button>
							</div>
						</div>
					</div>
				</section>
				<!-- Passive Control Section -->
				<section class="passive-control-section">
					<header class="section-header center">
						<h3 class="subsection-title">{{ $t('refrigeratedGoods.passiveControl.title') }}</h3>
						<p class="subsection-subtitle">{{ $t('refrigeratedGoods.passiveControl.subtitle') }}</p>
					</header>

					<div class="passive-options-grid">
						<div v-for="(option, index) in $t('refrigeratedGoods.passiveControl.options')" :key="`option-${index}`"
							class="option-card">
							<div class="option-header">
								<div class="option-icon" :style="{ color: option.color }">
									<img :src="getImage(option.icon)" class="icon-image" />
								</div>
								<h4 class="option-title">{{ option.title }}</h4>
							</div>
							<p class="option-desc">{{ option.description }}</p>
							<ul class="option-features">
								<li v-for="(feature, i) in option.features" :key="`feature-${i}`">
									{{ feature }}
								</li>
							</ul>
						</div>
					</div>
				</section>
			</div>
		</section>
	</div>
</template>

<script>
	export default {
		name: 'RefrigeratedGoods',
		methods: {
			getImage(filename) {
				try {
					return require(`@/assets/images/${filename}`);
				} catch (e) {
					console.warn(`图片加载失败: ${filename}`);
					return '';
				}
			},
			handleImageError(event) {
				event.target.style.opacity = '0.2';
				event.target.alt = '图片加载失败';
			}
		}
	};
</script>

<style lang="scss" scoped>
	/* 基础样式 */
	.temp-control-container {
		// padding: 60px 0;
		font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
		// background-color: #f9fbfd;
	}

	.container {
		max-width: 1200px;
		margin: 0 auto;
		padding: 0 20px;
	}

	.section-header {
		margin-bottom: 80px;

		&.center {
			text-align: center;
		}

		.section-title {
			font-size: 42px;
			font-weight: 600;
			margin-bottom: 15px;
			color: #1a3e72;
			position: relative;
			display: inline-block;

			&::after {
				content: '';
				position: absolute;
				bottom: -15px;
				left: 0;
				width: 80px;
				height: 4px;
				background: linear-gradient(to right, #1a6fdf, #4facfe);
				border-radius: 2px;
			}
		}

		.section-subtitle {
			font-size: 18px;
			color: #6c757d;
			font-weight: 300;
			letter-spacing: 1px;
		}
	}

	.solution-intro {
		max-width: 800px;
		margin: 0 auto 50px;
		text-align: center;
	}

	.intro-text {
		font-size: clamp(1rem, 1.5vw, 1.1rem);
		line-height: 1.6;
		color: #34495e;
	}

	/* 特性网格 */
	.features-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(250px, 100%), 1fr));
		gap: 30px;
		margin-bottom: 60px;
	}

	.feature-card {
		background: white;
		border-radius: 10px;
		padding: 30px 25px;
		text-align: center;
		box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
		transition: transform 0.3s ease, box-shadow 0.3s ease;

		&:hover {
			transform: translateY(-5px);
			box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
		}
	}

	.feature-icon {
		width: 70px;
		height: 70px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		margin: 0 auto 20px;
		color: white;
		font-size: 1.8rem;
	}

	.feature-title {
		font-size: clamp(1.1rem, 1.5vw, 1.3rem);
		color: #2c3e50;
		margin-bottom: 15px;
		font-weight: 600;
	}

	.feature-desc {
		font-size: clamp(0.85rem, 1.2vw, 0.95rem);
		color: #7f8c8d;
		line-height: 1.5;
	}

	/* 主动控制区 */
	/* Active Control Section */
	.active-control-section {
		margin-bottom: 80px;

		.section-header {
			margin-bottom: 50px;
		}
	}

	.subsection-title {
		font-size: clamp(1.5rem, 3vw, 1.8rem);
		color: #2c3e50;
		margin-bottom: 10px;
		font-weight: 600;
		text-align: center;

		&::after {
			content: '';
			display: block;
			width: 50px;
			height: 3px;
			background: #2c5b9a;
			margin: 15px auto;
		}
	}

	.subsection-subtitle {
		font-size: clamp(0.95rem, 1.5vw, 1.05rem);
		color: #666;
		margin-top: 15px;
		line-height: 1.6;
		max-width: 700px;
		margin-left: auto;
		margin-right: auto;
		text-align: center;
	}

	.equipment-showcase {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
		gap: 30px;

		@media (min-width: 1200px) {
			grid-template-columns: repeat(3, 1fr);
		}

		@media (max-width: 1199px) and (min-width: 768px) {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	.equipment-card {
		background: white;
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
		transition: all 0.3s ease;
		display: flex;
		flex-direction: column;
		height: 100%;

		&:hover {
			transform: translateY(-5px);
			box-shadow: 0 10px 25px rgba(0, 0, 0, 0.12);

			.equip-image {
				transform: scale(1.03);
			}
		}
	}

	.equip-image-container {
		position: relative;
		overflow: hidden;
		height: 200px;
		background: #f5f7fa;

		@media (max-width: 767px) {
			height: 180px;
		}
	}

	.image-wrapper {
		width: 100%;
		height: 100%;
	}

	.equip-image {
		width: 100%;
		height: 100%;
		object-fit: cover;
		transition: transform 0.5s ease;
	}

	.equip-info {
		padding: 10px 20px;
		flex-grow: 1;
		display: flex;
		flex-direction: column;
	}

	.equip-name {
		font-size: clamp(1.1rem, 1.5vw, 1.2rem);
		color: #2c3e50;
		margin-bottom: 12px;
		font-weight: 600;
		line-height: 1.3;
	}

	.equip-desc {
		font-size: clamp(0.85rem, 1.2vw, 0.95rem);
		color: #666;
		margin-bottom: 15px;
		line-height: 1.5;
		flex-grow: 1;
	}

	.equip-specs {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
		margin-top: 15px;
		margin-bottom: 15px;
	}

	.spec-item {
		font-size: clamp(0.75rem, 1vw, 0.85rem);
		background: #f8f9fa;
		padding: 5px 12px;
		border-radius: 20px;
		color: #34495e;
		display: flex;
		align-items: center;

		.spec-icon {
			margin-right: 5px;
			color: #2c5b9a;
			font-size: 0.9rem;
		}
	}

	.equip-cta {
		margin-top: 15px;
		background: #2c5b9a;
		color: white;
		border: none;
		padding: 8px 15px;
		border-radius: 4px;
		cursor: pointer;
		font-size: clamp(0.8rem, 1vw, 0.9rem);
		transition: all 0.3s ease;
		align-self: flex-start;

		&:hover {
			background: darken(#2c5b9a, 10%);
			transform: translateY(-2px);
		}
	}

	/* 响应式调整 */
	@media (max-width: 767px) {
		.active-control-section {
			margin-bottom: 60px;

			.section-header {
				margin-bottom: 30px;
			}
		}

		.equipment-showcase {
			gap: 20px;
		}

		.equip-image-container {
			height: 160px;
		}

		.equip-info {
			padding: 15px;
		}
	}

	/* 被动控制区 */
	.passive-control-section {
		margin-bottom: 60px;
	}

	.passive-options-grid {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
		gap: 30px;
		margin: 40px 0;
	}

	.option-card {
		background: white;
		border-radius: 10px;
		padding: 25px;
		box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
		transition: transform 0.3s ease, box-shadow 0.3s ease;

		&:hover {
			transform: translateY(-5px);
			box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
		}
	}

	.option-header {
		display: flex;
		align-items: center;
		margin-bottom: 15px;
	}

	.option-icon {
		width: 50px;
		height: 50px;
		border-radius: 50%;
		background: rgba(52, 152, 219, 0.1);
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.5rem;
		margin-right: 15px;
	}

	.icon-image {
		width: 28px;
		height: 28px;
		object-fit: contain;
	}

	.option-title {
		font-size: clamp(1.1rem, 1.5vw, 1.2rem);
		color: #2c3e50;
		font-weight: 600;
	}

	.option-desc {
		font-size: clamp(0.85rem, 1.2vw, 0.95rem);
		color: #7f8c8d;
		margin-bottom: 15px;
		line-height: 1.5;
	}

	.option-features {
		list-style-type: none;
		padding-left: 0;

		li {
			position: relative;
			padding-left: 20px;
			margin-bottom: 8px;
			font-size: clamp(0.8rem, 1.1vw, 0.9rem);
			color: #34495e;

			&::before {
				content: '✓';
				position: absolute;
				left: 0;
				color: #2ecc71;
				font-weight: bold;
			}
		}
	}

	/* 响应式调整 */
	@media (max-width: 767px) {
		.temp-control-container {
			padding: 40px 0;
		}

		.features-grid,
		.passive-options-grid {
			gap: 20px;
		}

		.feature-card {
			padding: 20px 15px;
		}

		.feature-icon {
			width: 60px;
			height: 60px;
			font-size: 1.5rem;
		}

		.equipment-showcase {
			gap: 20px;
		}

		.option-card {
			padding: 20px;
		}
	}
</style>